import React, { useState, useEffect } from "react";
import { Button, Table, Checkbox, Select, Input, Tag, Empty, Space } from "antd";
import { UserOutlined, AppstoreOutlined, CheckCircleOutlined, SearchOutlined } from "@ant-design/icons";
import { GeneralService } from "../Services/GeneralService";
import { useNavigate } from 'react-router-dom';

import { notify } from "../components/Customs/NotifyProvider";

import { canViewModule, canCreateModule, canUpdateModule, canDeleteModule } from "../../utils/permissionUtils";

const MODULE_KEY = "user_permissions";

const MOCK_MODULES = [];

function UserPermissionTab() {

    const navigate = useNavigate();
    const [selectedUserId, setSelectedUserId] = useState(undefined);
    const [searchText, setSearchText] = useState("");
    const [modules, setModules] = useState([]);
    const [users, setUsers] = useState([]);

    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const res = await GeneralService.get("users");
                setUsers(res.data || []);
            } catch (err) {
                console.error("Failed to fetch users", err);
            }
        };
        fetchUsers();
    }, []);


    useEffect(() => {
        const fetchModules = async () => {
            try {
                const res = await GeneralService.get("modules");
                const sortedModules = (res.data || []).sort((a, b) =>
                    a.module_name.localeCompare(b.module_name)
                );

                setModules(sortedModules);

                console.log("res.data", res.data);

            } catch (err) {
                console.error("Failed to fetch modules", err);
            }
        };
        fetchModules();
    }, []);

    useEffect(() => {
        if (modules.length === 0) return;
        const map = {};
        modules.forEach((m) => {
            map[m.module_key] = { can_view: false, can_create: false, can_update: false, can_delete: false };
        });
        setPermissionMap(map);
    }, [modules]);

    const [permissionMap, setPermissionMap] = useState(() => {
        const map = {};
        modules.forEach((m) => {
            map[m.module_key] = { can_view: false, can_create: false, can_update: false, can_delete: false };
        });
        return map;
    });

    const handlePermissionChange = (moduleKey, field, checked) => {
        setPermissionMap((prev) => ({
            ...prev,
            [moduleKey]: { ...prev[moduleKey], [field]: checked },
        }));
    };

    const handleRowAll = (moduleKey, checked) => {
        setPermissionMap((prev) => ({
            ...prev,
            [moduleKey]: { can_view: checked, can_create: checked, can_update: checked, can_delete: checked },
        }));
    };

    const handleEnableAll = () => {
        const map = {};
        modules.forEach((m) => {
            map[m.module_key] = { can_view: true, can_create: true, can_update: true, can_delete: true };
        });
        setPermissionMap(map);
    };

    const handleClearAll = () => {
        const map = {};
        modules.forEach((m) => {
            map[m.module_key] = { can_view: false, can_create: false, can_update: false, can_delete: false };
        });
        setPermissionMap(map);
    };

    const handleSave = async () => {
        if (!selectedUserId) {
            notify.warning("Please select a user first");
            return;
        }
        try {
            const permissions = modules.map((m) => ({
                module_key: m.module_key,
                can_view: permissionMap[m.module_key]?.can_view ? 1 : 0,
                can_create: permissionMap[m.module_key]?.can_create ? 1 : 0,
                can_update: permissionMap[m.module_key]?.can_update ? 1 : 0,
                can_delete: permissionMap[m.module_key]?.can_delete ? 1 : 0,
            }));
            const res = await GeneralService.saveUserPermissions(selectedUserId, permissions);
            if (res?.status) {
                notify.success("Permissions saved successfully");
            } else {
                notify.error(res?.message || "Failed to save");
            }
        } catch (err) {
            notify.error("Something went wrong");
        }
    };
    const filtered = modules.filter((m) => {
        const q = searchText.toLowerCase();
        return !q || m.module_name.toLowerCase().includes(q) || m.module_key.toLowerCase().includes(q);
    });

    const enabledCount = modules.filter((m) => {
        const p = permissionMap[m.module_key];
        return p?.can_view || p?.can_create || p?.can_update || p?.can_delete;
    }).length;

    const selectedUser = users.find((u) => u.id === selectedUserId);

    const columns = [
        {
            title: "Module", dataIndex: "module_name",
            render: (_, record) => (
                <div>
                    <div style={{ fontWeight: 600 }}>{record.module_name}</div>
                    <div style={{ fontSize: 11, color: "#888" }}>{record.module_key} • {record.route_path}</div>
                </div>
            ),
        },
        {
            title: "Status", width: 110, align: "center",
            render: (_, record) => {
                const p = permissionMap[record.module_key];
                const on = p?.can_view || p?.can_create || p?.can_update || p?.can_delete;
                return <Tag color={on ? "success" : "default"}>{on ? "Enabled" : "Disabled"}</Tag>;
            },
        },
        {
            title: "View", width: 90, align: "center",
            render: (_, record) => (
                <Checkbox checked={!!permissionMap[record.module_key]?.can_view}
                    onChange={(e) => handlePermissionChange(record.module_key, "can_view", e.target.checked)} />
            ),
        },
        {
            title: "Create", width: 90, align: "center",
            render: (_, record) => (
                <Checkbox checked={!!permissionMap[record.module_key]?.can_create}
                    onChange={(e) => handlePermissionChange(record.module_key, "can_create", e.target.checked)} />
            ),
        },
        {
            title: "Update", width: 90, align: "center",
            render: (_, record) => (
                <Checkbox checked={!!permissionMap[record.module_key]?.can_update}
                    onChange={(e) => handlePermissionChange(record.module_key, "can_update", e.target.checked)} />
            ),
        },
        {
            title: "Delete", width: 90, align: "center",
            render: (_, record) => (
                <Checkbox checked={!!permissionMap[record.module_key]?.can_delete}
                    onChange={(e) => handlePermissionChange(record.module_key, "can_delete", e.target.checked)} />
            ),
        },
        {
            title: "All", width: 90, align: "center",
            render: (_, record) => {
                const p = permissionMap[record.module_key] || {};
                return (
                    <Checkbox
                        checked={!!(p.can_view && p.can_create && p.can_update && p.can_delete)}
                        onChange={(e) => handleRowAll(record.module_key, e.target.checked)}
                    />
                );
            },
        },
    ];

    if (!canViewModule(MODULE_KEY)) {
        return <Empty description="No permission to view this page" />;
    }

    return (
        <div style={{ padding: "16px 0" }}>

            <div style={{ borderBottom: "1px solid #e2e8f0", marginBottom: 16, display: "flex" }}>
                <div
                    onClick={() => navigate("/user-access")}
                    style={{ padding: "12px 24px", cursor: "pointer", color: "#888", fontWeight: 500 }}
                >
                    Manage Users
                </div>
                <div style={{ padding: "12px 24px", cursor: "pointer", color: "#1677ff", fontWeight: 500, borderBottom: "2px solid #1677ff" }}>
                    User Permissions
                </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, marginBottom: 16 }}>
                {[
                    { icon: <AppstoreOutlined />, label: "Total Modules", value: modules.length, color: "#6366f1" },
                    { icon: <CheckCircleOutlined />, label: "Enabled Modules", value: enabledCount, color: "#22c55e" },
                    { icon: <UserOutlined />, label: "Selected User", value: selectedUser?.name || "None", color: "#ef4444" },
                ].map((s) => (
                    <div key={s.label} style={{ display: "flex", alignItems: "center", gap: 12, padding: 16, background: "#f8fafc", borderRadius: 10, border: "1px solid #e2e8f0" }}>
                        <div style={{ width: 40, height: 40, borderRadius: 10, background: s.color + "20", color: s.color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>{s.icon}</div>
                        <div>
                            <div style={{ fontSize: 11, color: "#888" }}>{s.label}</div>
                            <div style={{ fontSize: 18, fontWeight: 700 }}>{s.value}</div>
                        </div>
                    </div>
                ))}
            </div>

            <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10, padding: 16, marginBottom: 16 }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
                    <span style={{ fontWeight: 600 }}>Permission Filters</span>
                    <span style={{ color: "#888", fontSize: 12 }}>{filtered.length} modules found</span>
                </div>
                <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                    <div style={{ flex: 1, minWidth: 180 }}>
                        <div style={{ fontSize: 12, marginBottom: 4 }}>Select User</div>
                        <Select
                            placeholder="Choose user"
                            style={{ width: "100%" }}
                            value={selectedUserId}
                            allowClear
                            showSearch
                            filterOption={(input, option) =>
                                option?.label?.toLowerCase().includes(input.toLowerCase())
                            }
                            options={users.map((u) => ({
                                label: `${u.name} (${u.email})`,
                                value: u.id,
                            }))}
                            onChange={async (userId) => {
                                setSelectedUserId(userId);
                                if (!userId) return;
                                try {
                                    const res = await GeneralService.getUserPermissions(userId);
                                    const map = {};
                                    modules.forEach((m) => {
                                        map[m.module_key] = { can_view: false, can_create: false, can_update: false, can_delete: false };
                                    });
                                    (res.data || []).forEach((item) => {
                                        map[item.module_key] = {
                                            can_view: item.can_view === true || item.can_view === 't' || Number(item.can_view) === 1,
                                            can_create: item.can_create === true || item.can_create === 't' || Number(item.can_create) === 1,
                                            can_update: item.can_update === true || item.can_update === 't' || Number(item.can_update) === 1,
                                            can_delete: item.can_delete === true || item.can_delete === 't' || Number(item.can_delete) === 1,
                                        };
                                    });
                                    setPermissionMap(map);
                                } catch (err) {
                                    console.error("Failed to load permissions", err);
                                }
                            }}
                        />
                    </div>
                    <div style={{ flex: 2, minWidth: 220 }}>
                        <div style={{ fontSize: 12, marginBottom: 4 }}>Search Module</div>
                        <Input prefix={<SearchOutlined />} placeholder="Search by module name / key / route"
                            value={searchText} onChange={(e) => setSearchText(e.target.value)} />
                    </div>
                    <div>
                        <div style={{ fontSize: 12, marginBottom: 4 }}>Quick Action</div>
                        <Space>
                            <Button onClick={handleEnableAll} disabled={!canUpdateModule(MODULE_KEY)}>Enable All</Button>
                            <Button danger onClick={handleClearAll} disabled={!canDeleteModule(MODULE_KEY)}>Clear All</Button>
                            <Button type="primary" onClick={handleSave} disabled={!canCreateModule(MODULE_KEY)}>Save Permissions</Button>
                        </Space>
                    </div>
                </div>
            </div>

            <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: 16 }}>
                <div style={{ marginBottom: 12 }}>
                    <div style={{ fontWeight: 600 }}>Module Permissions</div>
                    <div style={{ fontSize: 12, color: "#888" }}>Configure view, create, update, and delete access for each module.</div>
                </div>
                {/* {!selectedUserId ? (
                    <Empty description="Please select a user to manage permissions" />
                ) : ( */}
                    <Table rowKey="module_key" columns={columns} dataSource={filtered} scroll={{ x: 800 }}
                        pagination={{
                            showSizeChanger: true,
                            // showQuickJumper: true,
                            showTotal: (total) => `Total ${total} records`,
                        }}
                    />
            </div>
        </div>
    );
}

export default UserPermissionTab;